/**
 * Главный компонент приложения
 * Здесь определяются маршруты (routes) между страницами
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Header from './components/Header';
import DocumentTitle from './components/DocumentTitle';
import LoginPage from './pages/LoginPage';
import WelcomePage from './pages/WelcomePage';
import DashboardOup4Page from './pages/DashboardOup4Page';
import { getAccessibleDashboards } from './utils/dashboards';
import ContractorsPage from './pages/ContractorsPage';
import ContractorDetailPage from './pages/ContractorDetailPage';
import ContractsPage from './pages/ContractsPage';
import InProgressContractsPage from './pages/InProgressContractsPage';
import InProgressTasksPage from './pages/InProgressTasksPage';
import ClosingTasksPage from './pages/ClosingTasksPage';
import ClosingContractsPage from './pages/ClosingContractsPage';
import ClosingStagesPage from './pages/ClosingStagesPage';
import ClosingManagementPage from './pages/ClosingManagementPage';
import ContractDetailPage from './pages/ContractDetailPage';
import TasksPage from './pages/TasksPage';
import TaskDetailPage from './pages/TaskDetailPage';
import IntoWorkPage from './pages/IntoWorkPage';
import OperationsPage from './pages/OperationsPage';
import PaymentRequestPage from './pages/PaymentRequestPage';
import PaymentRequestsViewPage from './pages/PaymentRequestsViewPage';
import PaymentApprovalsPage from './pages/PaymentApprovalsPage';
import PaymentIssuesPage from './pages/PaymentIssuesPage';
import PaymentDistributionPage from './pages/PaymentDistributionPage';
import PaymentViewPage from './pages/PaymentViewPage';
import ArticlesPage from './pages/ArticlesPage';
import TaxesPage from './pages/TaxesPage';
import CashRatesPage from './pages/CashRatesPage';
import ServFeeRatesPage from './pages/ServFeeRatesPage';
import AccessControlPage from './pages/AccessControlPage';
import ArticleRolesAccessPage from './pages/ArticleRolesAccessPage';
import './App.css';

// ==========================================
// ЗАЩИЩЁННЫЙ МАРШРУТ (ProtectedRoute)
// ==========================================

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div className="loading">⏳ Загрузка...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const AdminRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, can } = useAuth();

  if (isLoading) {
    return <div className="loading">⏳ Загрузка...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!can('ACCESS_ADMIN', 'EDIT')) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const OupDashboardRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, hasDepartment } = useAuth();

  if (isLoading) {
    return <div className="loading">⏳ Загрузка...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!hasDepartment('OUP_UL')) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const HomeLanding: React.FC = () => {
  const { hasDepartment } = useAuth();
  const dashboards = getAccessibleDashboards(hasDepartment);

  if (dashboards.length > 0) {
    return <Navigate to={dashboards[0].to} replace />;
  }

  return <WelcomePage />;
};

const MasterdataRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, can } = useAuth();

  if (isLoading) {
    return <div className="loading">⏳ Загрузка...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!can('MASTERDATA', 'EDIT')) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// ==========================================
// ГЛАВНОЕ ПРИЛОЖЕНИЕ
// ==========================================

const AppContent: React.FC = () => {
  const { isAuthenticated } = useAuth();

  const mainClassName = isAuthenticated ? 'app-content with-sidebar' : 'app-content';

  return (
    <div className={`app-shell${isAuthenticated ? ' app-shell--authenticated' : ''}`}>
      <DocumentTitle />
      {isAuthenticated && (
        <Header />
      )}

      <main className={mainClassName}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <HomeLanding />
              </ProtectedRoute>
            }
          />
          <Route path="/dashboard-oup" element={<Navigate to="/dashboard-oup4" replace />} />
          <Route path="/dashboard-oup2" element={<Navigate to="/dashboard-oup4" replace />} />
          <Route path="/dashboard-oup3" element={<Navigate to="/dashboard-oup4" replace />} />
          <Route
            path="/dashboard-oup4"
            element={
              <OupDashboardRoute>
                <DashboardOup4Page />
              </OupDashboardRoute>
            }
          />
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          <Route
            path="/contractors"
            element={
              <ProtectedRoute>
                <ContractorsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contractors/:id"
            element={
              <ProtectedRoute>
                <ContractorDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contracts"
            element={
              <ProtectedRoute>
                <ContractsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contracts/:id"
            element={
              <ProtectedRoute>
                <ContractDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/in-progress/contracts"
            element={
              <ProtectedRoute>
                <InProgressContractsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/in-progress/tasks"
            element={
              <ProtectedRoute>
                <InProgressTasksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/closing/tasks"
            element={
              <ProtectedRoute>
                <ClosingTasksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/closing/contracts"
            element={
              <ProtectedRoute>
                <ClosingContractsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/closing/stages"
            element={
              <ProtectedRoute>
                <ClosingStagesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/closing/managment"
            element={
              <ProtectedRoute>
                <ClosingManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/register/create"
            element={
              <ProtectedRoute>
                <IntoWorkPage />
              </ProtectedRoute>
            }
          />
          <Route path="/into-work" element={<Navigate to="/register/create" replace />} />
          <Route
            path="/tasks"
            element={
              <ProtectedRoute>
                <TasksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks/:id"
            element={
              <ProtectedRoute>
                <TaskDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/operations"
            element={
              <ProtectedRoute>
                <OperationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/finance/payment-requests"
            element={
              <ProtectedRoute>
                <PaymentRequestPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/finance/payment-requests-view"
            element={
              <ProtectedRoute>
                <PaymentRequestsViewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/finance/payment-approvals"
            element={
              <ProtectedRoute>
                <PaymentApprovalsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/finance/payment-issues"
            element={
              <ProtectedRoute>
                <PaymentIssuesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/finance/payment-distribution"
            element={
              <ProtectedRoute>
                <PaymentDistributionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/finance/payment-view"
            element={
              <ProtectedRoute>
                <PaymentViewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/masterdata/articles"
            element={
              <MasterdataRoute>
                <ArticlesPage />
              </MasterdataRoute>
            }
          />
          <Route
            path="/settings/masterdata/taxes"
            element={
              <MasterdataRoute>
                <TaxesPage />
              </MasterdataRoute>
            }
          />
          <Route
            path="/settings/masterdata/cash"
            element={
              <MasterdataRoute>
                <CashRatesPage />
              </MasterdataRoute>
            }
          />
          <Route
            path="/settings/masterdata/serv-fee"
            element={
              <MasterdataRoute>
                <ServFeeRatesPage />
              </MasterdataRoute>
            }
          />
          <Route
            path="/admin/access"
            element={
              <AdminRoute>
                <AccessControlPage />
              </AdminRoute>
            }
          />
          <Route
            path="/settings/access/articles"
            element={
              <AdminRoute>
                <ArticleRolesAccessPage />
              </AdminRoute>
            }
          />

        </Routes>
      </main>
    </div>
  );
};

// ==========================================
// ОСНОВНОЙ КОМПОНЕНТ
// ==========================================

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
};

export default App;